import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styles from "./Blog.module.css";
import NewsletterSignup from "../ui/NewsletterSignup.js";
import { POSTS_PER_PAGE, blogPagePath, totalBlogPages } from "../blogPagination.js";
import { preloaded, capture } from "../ui/preloadedState.js";

export default function Blog({ languageEn }) {
  const { page: pageParam } = useParams();

  const lang = languageEn ? "en" : "bg";
  const prefix = languageEn ? "/en" : "";

  // Seeded from the prerender so the first client render is the finished list,
  // not an empty one — an empty first render would not match the static HTML.
  const preloadedPosts = preloaded(`blog:${lang}`);
  const [posts, setPosts] = useState(preloadedPosts || []);
  const [loading, setLoading] = useState(!preloadedPosts);

  const currentPage = Math.max(1, parseInt(pageParam, 10) || 1);
  const totalPages = totalBlogPages(posts.length);
  const visiblePosts = posts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const text = {
    en: {
      title: "Blog",
      metaTitle: "Blog - Vkushty",
      metaDescription:
        "News, updates and insights from the Vkushty luxury community in Bistritsa, Sofia.",
      readMore: "Read more",
      empty: "No posts yet. Stay tuned!",
      pageLabel: "Page",
      prev: "Previous",
      next: "Next",
      pagination: "Blog pages",
    },
    bg: {
      title: "Блог",
      metaTitle: "Блог - Вкъщи",
      metaDescription:
        "Новини, актуализации и полезна информация от луксозния комплекс Вкъщи в Бистрица, София.",
      readMore: "Прочети повече",
      empty: "Все още няма публикации. Следете ни!",
      pageLabel: "Страница",
      prev: "Назад",
      next: "Напред",
      pagination: "Страници на блога",
    },
  };

  const t = languageEn ? text.en : text.bg;

  useEffect(() => {
    fetch("/content/blog/index.json")
      .then((res) => res.json())
      .then((data) => {
        // Only show posts whose date has arrived — future-dated posts stay
        // hidden from the listing until their day, so they self-publish one
        // by one (evaluated on every load, no rebuild needed).
        const now = Date.now();
        const published = data.filter(
          (p) => p.lang === lang && new Date(p.date).getTime() <= now
        );
        capture(`blog:${lang}`, published);
        setPosts(published);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [lang]);

  const canonicalPath = blogPagePath(prefix, currentPage);
  const pageTitle =
    currentPage > 1 ? `${t.metaTitle} — ${t.pageLabel} ${currentPage}` : t.metaTitle;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(languageEn ? "en-GB" : "bg-BG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className={styles.wrap}>
      <Helmet defer={false}>
        <html lang={languageEn ? "en" : "bg"} />
        {/* Every page past the first needs its own title and canonical, or all
            of them look like duplicates of /blog/ to a crawler. */}
        <title>{pageTitle}</title>
        <meta name="description" content={t.metaDescription} />
        <link rel="canonical" href={`https://vkushty.com${canonicalPath}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t.metaDescription} />
        <meta property="og:url" content={`https://vkushty.com${canonicalPath}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={t.metaDescription} />
        {currentPage > 1 && (
          <link
            rel="prev"
            href={`https://vkushty.com${blogPagePath(prefix, currentPage - 1)}`}
          />
        )}
        {currentPage < totalPages && (
          <link
            rel="next"
            href={`https://vkushty.com${blogPagePath(prefix, currentPage + 1)}`}
          />
        )}
        {/* Pair each page with the same page of the other language, not with
            the other language's page 1. */}
        <link rel="alternate" hreflang="bg" href={`https://vkushty.com${blogPagePath("", currentPage)}`} />
        <link rel="alternate" hreflang="en" href={`https://vkushty.com${blogPagePath("/en", currentPage)}`} />
        <link rel="alternate" hreflang="x-default" href={`https://vkushty.com${blogPagePath("", currentPage)}`} />
        <meta property="og:locale" content={languageEn ? "en_US" : "bg_BG"} />
        <meta property="og:locale:alternate" content={languageEn ? "bg_BG" : "en_US"} />
      </Helmet>

      <div className={styles.container}>
        <h1 className={styles.heading}>{t.title}</h1>

        {loading ? (
          <div className={styles.loading} />
        ) : visiblePosts.length === 0 ? (
          <>
            <div id="snap-ready" style={{ display: "none" }} />
            <p className={styles.empty}>{t.empty}</p>
          </>
        ) : (
          <>
            <div id="snap-ready" style={{ display: "none" }} />
            <div className={styles.grid}>
            {visiblePosts.map((post) => (
              <Link
                key={post.slug}
                to={`${prefix}/blog/${post.slug}`}
                className={styles.card}
              >
                {post.thumbnail && (
                  <div className={styles.thumbnailWrap}>
                    <img
                      // Index cards are ~400px wide; thumbnailCard avoids
                      // pulling the full-size hero for a small card.
                      src={post.thumbnailCard || post.thumbnail}
                      alt={post.thumbnailAlt || post.title}
                      className={styles.thumbnail}
                      loading="lazy"
                    />
                  </div>
                )}
                <div className={styles.cardBody}>
                  <time className={styles.date}>{formatDate(post.date)}</time>
                  <h2 className={styles.cardTitle}>{post.title}</h2>
                  <p className={styles.description}>{post.description}</p>
                  <span className={styles.readMore}>{`${t.readMore} →`}</span>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <nav className={styles.pagination} aria-label={t.pagination}>
              {currentPage > 1 && (
                <Link
                  to={blogPagePath(prefix, currentPage - 1)}
                  className={styles.pageNav}
                  rel="prev"
                >
                  {`← ${t.prev}`}
                </Link>
              )}

              <ol className={styles.pageList}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) =>
                  n === currentPage ? (
                    <li key={n}>
                      <span className={styles.pageCurrent} aria-current="page">
                        {n}
                      </span>
                    </li>
                  ) : (
                    <li key={n}>
                      <Link
                        to={blogPagePath(prefix, n)}
                        className={styles.pageLink}
                        aria-label={`${t.pageLabel} ${n}`}
                      >
                        {n}
                      </Link>
                    </li>
                  )
                )}
              </ol>

              {currentPage < totalPages && (
                <Link
                  to={blogPagePath(prefix, currentPage + 1)}
                  className={styles.pageNav}
                  rel="next"
                >
                  {`${t.next} →`}
                </Link>
              )}
            </nav>
          )}
          </>
        )}

        <NewsletterSignup languageEn={languageEn} source="blog_list" variant="inline" />
      </div>
    </div>
  );
}
